/**
 * RBAC machine — droits des clients qui n'existent que par l'API (CPI-Global en cabine,
 * Checkpoint/PopFrais).
 *
 * Même mécanique que la v2 (`./rbac.v2.ts`) et même garde (`middleware.rbac`), mais deux
 * différences irréductibles :
 * - les droits ne viennent PAS d'un rôle : ils sont portés par la méthode d'authentification
 *   (colonne `abilities` de la clé d'API), si bien qu'une clé compromise reste bornée à son usage ;
 * - la taxonomie n'a volontairement aucune map de libellés : c'est ce qui garantit qu'elle ne peut
 *   pas être rendue dans la matrice de permissions du back-office.
 *
 * Le namespace `machine.` est disjoint de la v1 (ressource plate) et de la v2
 * (`module.sous-onglet.fonctionnalité`) : une ligne `role_permissions` ne peut donc jamais être
 * lue comme une ability machine, ni l'inverse.
 */

import type { RbacV2AbilityTuple } from './rbac.v2.js'

/** Actions métier des clients machine (aucun recoupement avec les actions v1/v2). */
export const RBAC_MACHINE_ACTIONS = [
  'compute',
  'cancel',
  'pay',
  'validate',
  'read-history',
  'read-advanced-infos',
  'close',
  'ask-open',
  'open',
  'read-state',
  'read-config',
  'write-config',
  'report-event',
  'confirm',
  'destroy',
  'read',
  'link-epc',
] as const

export type RbacMachineAction = (typeof RBAC_MACHINE_ACTIONS)[number]

/**
 * OBJET GÉNÉRAL — source de vérité de la taxonomie machine.
 * Clé = `machine.<client>.<domaine>`, valeur = tableau exact des actions de la ressource.
 */
export const rbacMachineAbilities = {
  'machine.cpi.cart': ['compute', 'cancel', 'pay', 'validate'],
  'machine.cpi.article': ['read-history', 'read-advanced-infos'],
  'machine.cpi.pre-authorization': ['close'],
  'machine.cpi.door': [
    'ask-open',
    'open',
    'read-state',
    'read-config',
    'write-config',
    'report-event',
    'confirm',
  ],
  'machine.checkpoint.unique-article': ['read-state', 'destroy'],
  'machine.checkpoint.lot': ['read', 'link-epc'],
} as const satisfies Record<`machine.${string}`, readonly RbacMachineAction[]>

export type RbacMachineResource = keyof typeof rbacMachineAbilities

export type RbacMachineAbilityTuple = {
  [K in RbacMachineResource]: [(typeof rbacMachineAbilities)[K][number], K]
}[RbacMachineResource]

export const rbacMachineResources = Object.keys(rbacMachineAbilities) as RbacMachineResource[]

/** Tuple `[action, ressource]` acceptable par `middleware.rbac(...)`, v2 ou machine. */
export type RbacGuardableTuple = RbacV2AbilityTuple | RbacMachineAbilityTuple

export type RbacMachineAbilityKey = {
  [K in RbacMachineResource]: `${K}:${(typeof rbacMachineAbilities)[K][number]}`
}[RbacMachineResource]

/** Forme stockée dans `admin_api_keys.abilities` — et énumération du `vine.enum` d'écriture. */
export const rbacMachineAbilityKeys = rbacMachineResources.flatMap((resource) =>
  (rbacMachineAbilities[resource] as readonly RbacMachineAction[]).map(
    (action) => `${resource}:${action}`,
  ),
) as RbacMachineAbilityKey[]

const machineAbilityKeySet = new Set<string>(rbacMachineAbilityKeys)

/**
 * Une clé inconnue — et notamment le `"*"` hérité du défaut des tokens AdonisJS — n'accorde rien.
 */
export const parseRbacMachineAbilityKey = (raw: string): RbacMachineAbilityTuple | null => {
  if (!machineAbilityKeySet.has(raw)) return null

  const separator = raw.lastIndexOf(':')
  return [raw.slice(separator + 1), raw.slice(0, separator)] as RbacMachineAbilityTuple
}
